// src/Dashboard/pages/teacher/GradeCN/components/GradeCNDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaSave, FaTimes, FaUserGraduate } from "react-icons/fa";
import { toast } from "react-toastify";

import { getAdherentsWithBrownBeltAndAbove } from "../../../../../services/beltServices";

import {
  DEFAULT_USER_PHOTO,
  DOMINANTE_OPTIONS,
  getAdherentName,
  getAdherentPhoto,
  getEmptyGradeForm,
  getUvCompletion,
  normalizeGradeForForm,
} from "../utils/gradeCNDashboardUtils";

function ToggleBox({ label, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`rounded-2xl border px-4 py-3 text-left transition ${
        checked
          ? "border-emerald-300 bg-emerald-50 text-emerald-700 shadow-sm dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-bold">{label}</span>

        <span
          className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
            checked
              ? "bg-emerald-600 text-white"
              : "bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-300"
          }`}
        >
          {checked ? "✓" : "—"}
        </span>
      </div>
    </button>
  );
}

export default function GradeCNDrawer({
  isOpen,
  mode = "create",
  grade,
  saving,
  onClose,
  onSubmit,
}) {
  const [formValues, setFormValues] = useState(getEmptyGradeForm());
  const [adherents, setAdherents] = useState([]);
  const [loadingAdherents, setLoadingAdherents] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (mode === "edit" && grade) {
      setFormValues(normalizeGradeForForm(grade));
    } else {
      setFormValues(getEmptyGradeForm());
    }
  }, [isOpen, mode, grade]);

  useEffect(() => {
    if (!isOpen || mode !== "create") return;

    let cancelled = false;

    const loadAdherents = async () => {
      try {
        setLoadingAdherents(true);

        const list = await getAdherentsWithBrownBeltAndAbove();

        if (cancelled) return;

        const sorted = (Array.isArray(list) ? list : []).slice().sort((a, b) => {
          const byNom = String(a.nom || "").localeCompare(String(b.nom || ""), "fr", {
            sensitivity: "base",
          });

          if (byNom !== 0) return byNom;

          return String(a.prenom || "").localeCompare(String(b.prenom || ""), "fr", {
            sensitivity: "base",
          });
        });

        setAdherents(sorted);
      } catch (error) {
        console.error("Erreur chargement adhérents marron et + :", error);
        toast.error("Erreur lors du chargement des adhérents marron et plus.");
        setAdherents([]);
      } finally {
        if (!cancelled) {
          setLoadingAdherents(false);
        }
      }
    };

    loadAdherents();

    return () => {
      cancelled = true;
    };
  }, [isOpen, mode]);

  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [isOpen, saving, onClose]);

  const title = mode === "edit" ? "Modifier un Grade CN" : "Créer un Grade CN";

  const subtitle =
    mode === "edit"
      ? "Mettez à jour la dominante, les UV, les points et les observations."
      : "Sélectionnez un adhérent marron ou plus, puis renseignez son suivi CN.";

  const adherentFromGrade = grade?.Adherent || null;

  const selectedAdherent = useMemo(() => {
    if (mode === "edit") return adherentFromGrade;

    return adherents.find(
      (adherent) => String(adherent.id) === String(formValues.adherent_id)
    );
  }, [mode, adherentFromGrade, adherents, formValues.adherent_id]);

  const completion = useMemo(() => {
    return getUvCompletion(formValues);
  }, [formValues]);

  const updateField = (key, value) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const canSubmit =
    formValues.adherent_id && formValues.dominante && !saving && !loadingAdherents;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formValues.adherent_id) {
      toast.warn("Sélectionnez un adhérent.");
      return;
    }

    if (!formValues.dominante) {
      toast.warn("Sélectionnez une dominante.");
      return;
    }

    await onSubmit?.(formValues);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-4xl flex-col bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              {mode === "edit" ? "Modification" : "Nouveau dossier"}
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              {title}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {subtitle}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <form
          id="gradeCNDrawerForm"
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-5"
        >
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr,300px]">
            <div className="space-y-5">
              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Candidat
                </p>

                {mode === "create" ? (
                  <div className="mt-4">
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Adhérent marron ou plus *
                    </label>

                    <select
                      value={formValues.adherent_id}
                      onChange={(event) =>
                        updateField("adherent_id", event.target.value)
                      }
                      disabled={loadingAdherents || saving}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                      required
                    >
                      <option value="">
                        {loadingAdherents
                          ? "Chargement..."
                          : "Sélectionner un adhérent"}
                      </option>

                      {adherents.map((adherent) => (
                        <option key={adherent.id} value={adherent.id}>
                          {String(adherent.nom || "").toUpperCase()}{" "}
                          {adherent.prenom || ""}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="mt-4 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
                    <img
                      src={getAdherentPhoto(selectedAdherent)}
                      alt={getAdherentName(selectedAdherent)}
                      onError={(event) => {
                        event.currentTarget.src = DEFAULT_USER_PHOTO;
                      }}
                      className="h-14 w-14 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                    />

                    <div className="min-w-0">
                      <p className="truncate font-bold text-slate-900 dark:text-white">
                        {getAdherentName(selectedAdherent) || "—"}
                      </p>

                      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        ID adhérent : {formValues.adherent_id || "—"}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Dominante
                </p>

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {DOMINANTE_OPTIONS.map((dominante) => {
                    const active = formValues.dominante === dominante;

                    return (
                      <button
                        key={dominante}
                        type="button"
                        onClick={() => updateField("dominante", dominante)}
                        className={`rounded-2xl border px-4 py-3 text-left transition ${
                          active
                            ? "border-[#013E8E] bg-[#013E8E] text-white shadow-sm"
                            : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                        }`}
                      >
                        <span className="text-sm font-bold">{dominante}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Unités de valeur
                </p>

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <ToggleBox
                    label="UV1"
                    checked={!!formValues.uv1}
                    onChange={(value) => updateField("uv1", value)}
                  />

                  <ToggleBox
                    label="UV2"
                    checked={!!formValues.uv2}
                    onChange={(value) => updateField("uv2", value)}
                  />

                  <ToggleBox
                    label="UV3"
                    checked={!!formValues.uv3}
                    onChange={(value) => updateField("uv3", value)}
                  />

                  <ToggleBox
                    label="UV4"
                    checked={!!formValues.uv4}
                    onChange={(value) => updateField("uv4", value)}
                  />
                </div>

                <div className="mt-4">
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Points UV3
                  </label>

                  <input
                    type="number"
                    min="0"
                    value={formValues.uv3_points}
                    onChange={(event) =>
                      updateField("uv3_points", event.target.value)
                    }
                    placeholder="0"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                  />
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Observations
                </label>

                <textarea
                  value={formValues.observations}
                  onChange={(event) =>
                    updateField("observations", event.target.value)
                  }
                  rows={5}
                  placeholder="Notes, points à travailler, échéances..."
                  className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                />
              </div>
            </div>

            <aside className="space-y-4">
              <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="bg-slate-50 p-4 dark:bg-slate-800/40">
                  <div className="flex items-center gap-3">
                    {selectedAdherent ? (
                      <img
                        src={getAdherentPhoto(selectedAdherent)}
                        alt={getAdherentName(selectedAdherent)}
                        onError={(event) => {
                          event.currentTarget.src = DEFAULT_USER_PHOTO;
                        }}
                        className="h-14 w-14 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                      />
                    ) : (
                      <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-300">
                        <FaUserGraduate />
                      </span>
                    )}

                    <div className="min-w-0">
                      <p className="truncate font-bold text-slate-900 dark:text-white">
                        {selectedAdherent
                          ? getAdherentName(selectedAdherent)
                          : "Aucun adhérent sélectionné"}
                      </p>

                      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        Dossier Grade CN
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 p-4">
                  <div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]"
                        style={{ width: `${completion.percent}%` }}
                      />
                    </div>

                    <p className="mt-1 text-xs font-bold text-slate-500 dark:text-slate-400">
                      {completion.done}/{completion.total} UV ·{" "}
                      {completion.percent}%
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-800/60">
                      <p className="font-bold text-slate-500 dark:text-slate-400">
                        Dominante
                      </p>
                      <p className="mt-1 font-bold text-slate-900 dark:text-white">
                        {formValues.dominante || "—"}
                      </p>
                    </div>

                    <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-800/60">
                      <p className="font-bold text-slate-500 dark:text-slate-400">
                        Points UV3
                      </p>
                      <p className="mt-1 font-bold text-slate-900 dark:text-white">
                        {Number(formValues.uv3_points || 0)}
                      </p>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/60">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      UV validées
                    </p>

                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs font-bold">
                      <span>{formValues.uv1 ? "✓" : "—"} UV1</span>
                      <span>{formValues.uv2 ? "✓" : "—"} UV2</span>
                      <span>{formValues.uv3 ? "✓" : "—"} UV3</span>
                      <span>{formValues.uv4 ? "✓" : "—"} UV4</span>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </form>

        <div className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="submit"
              form="gradeCNDrawerForm"
              disabled={!canSubmit}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSave />
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}